/**
 * Fleet Timeline Visualization Component
 * 
 * Displays a read-only timeline grid showing vehicle operating hours and breaks
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Tooltip, Divider } from '@mui/material';

import { FleetTimelineVisualizationProps } from './types';
import {
  parseTime,
  formatTime,
  calculateTimeDifference,
  getTimeLabels,
} from './timeUtils';

import { Icon } from '@liftango/ui-icons';
const FleetTimelineVisualization: React.FC<FleetTimelineVisualizationProps> = ({
  vehicles,
  serviceStartMinutes,
  serviceEndMinutes,
  intervalMinutes,
  peakPeriods,
  interactive = false,
  onBlockClick,
  renderVehicleCell,
  vehicleColumnWidthPx: vehicleColumnWidthPxOverride,
  renderVehicleRowSuffix,
  rowSuffixWidthPx,
}) => {
  const { t } = useTranslation('mapWizards');
  // Generate time labels for the header
  const timeLabels = getTimeLabels(serviceStartMinutes, serviceEndMinutes, intervalMinutes);
  const totalMinutes = calculateTimeDifference(serviceStartMinutes, serviceEndMinutes);
  const numBlocks = Math.ceil(totalMinutes / intervalMinutes);
  const vehicleColumnWidthPx = vehicleColumnWidthPxOverride ?? 180;
  const suffixWidthPx = rowSuffixWidthPx ?? 48;
  const hasRowSuffix = Boolean(renderVehicleRowSuffix);
  const suffixSegment = hasRowSuffix ? ` ${suffixWidthPx}px` : '';
  const timelineGridTemplate = `${vehicleColumnWidthPx}px repeat(${numBlocks}, minmax(0, 1fr))${suffixSegment}`;
  const timeLabelStride = numBlocks > 36 ? 4 : numBlocks > 24 ? 3 : numBlocks > 16 ? 2 : 1;

  // Check if a time block falls within peak period
  const isPeakBlock = (blockStartMinutes: number): boolean => {
    return peakPeriods.some(peak => {
      const peakStartMinutes = parseTime(peak.start);
      const peakEndMinutes = parseTime(peak.end);
      return blockStartMinutes >= peakStartMinutes && blockStartMinutes < peakEndMinutes;
    });
  };

  // Get vehicle status for a specific time block
  const getBlockStatus = (
    vehicleIdx: number,
    blockStartMinutes: number
  ): { status: 'working' | 'break' | 'off'; shiftIndex: number } => {
    const vehicle = vehicles[vehicleIdx];
    const blockEndMinutes = blockStartMinutes + intervalMinutes;

    // Check all operating periods for this vehicle
    for (let shiftIdx = 0; shiftIdx < vehicle.operatingPeriods.length; shiftIdx++) {
      const period = vehicle.operatingPeriods[shiftIdx];
      const periodStart = parseTime(period.start);
      const periodEnd = parseTime(period.end);

      // Half-open [start,end) prevents boundary slots from being forced active.
      if (blockStartMinutes >= periodStart && blockStartMinutes < periodEnd) {
        // Check if block overlaps with a break
        if (period.breaks) {
          for (const breakPeriod of period.breaks) {
            const breakStart = parseTime(breakPeriod.start);
            const breakEnd = parseTime(breakPeriod.end);

            // Check if block overlaps with break period
            // Block overlaps if: blockStart < breakEnd AND blockEnd > breakStart
            if (blockStartMinutes < breakEnd && blockEndMinutes > breakStart) {
              return { status: 'break', shiftIndex: shiftIdx };
            }
          }
        }

        // No break found, vehicle is working
        return { status: 'working', shiftIndex: shiftIdx };
      }
    }

    return { status: 'off', shiftIndex: -1 };
  };

  // Calculate total vehicles working per time block
  const getVehiclesWorkingAtBlock = (blockStartMinutes: number): number => {
    let count = 0;
    for (let i = 0; i < vehicles.length; i++) {
      const { status } = getBlockStatus(i, blockStartMinutes);
      if (status === 'working') {
        count++;
      }
    }
    return count;
  };

  // Get color for different shifts (cycle through colors)
  const getShiftColor = (shiftIndex: number): string => {
    const colors = [
      '#4caf50', // Green
      '#2196f3', // Blue  
      '#9c27b0', // Purple
      '#00bcd4', // Cyan
      '#f44336', // Red
      '#ffc107', // Amber
    ];
    return colors[shiftIndex % colors.length];
  };

  // Get status color
  const getStatusColor = (status: 'working' | 'break' | 'off', shiftIndex: number): string => {
    if (status === 'working') {
      return getShiftColor(shiftIndex);
    } else if (status === 'break') {
      return '#ff9800'; // Orange for breaks
    }
    return '#f5f5f5'; // Light gray for off
  };

  // Get status icon
  const getStatusIcon = (status: 'working' | 'break' | 'off') => {
    if (status === 'working') return <Icon size={20} name="briefcase" />;
    if (status === 'break') return <Icon size={20} name="coffee" />;
    return <Icon size={20} name="hourglass-half" />;
  };

  const handleBlockInteraction = (
    vehicleId: string,
    blockStartMinutes: number,
    status: 'working' | 'break' | 'off'
  ) => {
    if (!interactive || !onBlockClick) {
      return;
    }
    onBlockClick(vehicleId, blockStartMinutes, status);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('autoFleetGen.timeline.title')}
      </Typography>

      {/* Legend */}
      <Box display="flex" gap={1.25} mb={1.25} flexWrap="wrap">
        <Typography variant="caption" sx={{ fontWeight: 600, mr: 1 }}>
          {t('autoFleetGen.timeline.shiftColors')}
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box width={20} height={20} bgcolor="#4caf50" borderRadius={1} />
          <Typography variant="caption">{t('autoFleetGen.timeline.shift1')}</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box width={20} height={20} bgcolor="#2196f3" borderRadius={1} />
          <Typography variant="caption">{t('autoFleetGen.timeline.shift2')}</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box width={20} height={20} bgcolor="#9c27b0" borderRadius={1} />
          <Typography variant="caption">{t('autoFleetGen.timeline.shift3Plus')}</Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box width={20} height={20} bgcolor="#ff9800" borderRadius={1} />
          <Typography variant="caption">{t('autoFleetGen.timeline.break')}</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box width={20} height={20} bgcolor="#f5f5f5" border="1px solid #e0e0e0" borderRadius={1} />
          <Typography variant="caption">{t('autoFleetGen.timeline.off')}</Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box width={20} height={20} border="3px solid #000" borderRadius={1} />
          <Typography variant="caption">{t('autoFleetGen.timeline.peakPeriod')}</Typography>
        </Box>
      </Box>

      {/* Timeline Grid */}
      <Paper sx={{ p: 1.5, overflowX: 'hidden' }}>
        <Box sx={{ width: '100%' }}>
          {/* Header with time labels */}
          <Box display="grid" gridTemplateColumns={timelineGridTemplate} gap={0.5} mb={1}>
            <Box /> {/* Empty cell for vehicle names column */}
            {timeLabels.slice(0, -1).map((label, idx) => (
              <Box
                key={idx}
                sx={{
                  py: 0.5,
                  borderRadius: 1,
                  position: 'relative',
                  minHeight: '20px',
                }}
              >
                <Typography 
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    whiteSpace: 'nowrap',
                    fontSize: '0.6rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    visibility: idx % timeLabelStride === 0 ? 'visible' : 'hidden',
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
            {hasRowSuffix && <Box /> /* Empty cell aligning with the trailing suffix column */}
          </Box>

          {/* Vehicle rows */}
          {vehicles.map((vehicle, vehicleIdx) => {
            const colorIndex = vehicleIdx % 6; // Cycle through 6 colors
            const colors = ['#2196f3', '#9c27b0', '#ff9800', '#4caf50', '#f44336', '#00bcd4'];
            const vehicleColor = colors[colorIndex];

            return (
              <Box
                key={vehicle.vehicleId}
                display="grid"
                gridTemplateColumns={timelineGridTemplate}
                gap={0.5}
                mb={0.25}
                alignItems="center"
              >
                {/* Vehicle cell — default is just the name, but consumers can render richer per-vehicle controls. */}
                {renderVehicleCell ? (
                  renderVehicleCell(vehicle, vehicleIdx)
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{
                      bgcolor: '#fafafa',
                      px: 1,
                      py: 0.35,
                      borderRadius: 1,
                      border: `2px solid ${vehicleColor}`,
                      minHeight: 28,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500, color: vehicleColor, fontSize: '0.9rem' }}>
                      {vehicle.vehicleName}
                    </Typography>
                  </Box>
                )}

                {/* Time blocks */}
                {Array.from({ length: numBlocks }).map((_, blockIdx) => {
                  const blockStartMinutes = serviceStartMinutes + blockIdx * intervalMinutes;
                  const { status, shiftIndex } = getBlockStatus(vehicleIdx, blockStartMinutes);
                  const isPeak = isPeakBlock(blockStartMinutes);
                  const color = getStatusColor(status, shiftIndex);

                  return (
                    <Tooltip
                      key={blockIdx}
                      title={
                        <Box>
                          <Typography variant="caption" display="block">
                            {formatTime(blockStartMinutes)} - {formatTime(blockStartMinutes + intervalMinutes)}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
                            {status === 'working' && shiftIndex >= 0
                              ? t('autoFleetGen.timeline.shiftWorking', { shift: shiftIndex + 1 })
                              : status === 'working'
                              ? t('autoFleetGen.timeline.working')
                              : status === 'break'
                              ? t('autoFleetGen.timeline.break')
                              : t('autoFleetGen.timeline.off')}
                          </Typography>
                          {isPeak && (
                            <Typography variant="caption" display="block" color="warning.light">
                              {t('autoFleetGen.timeline.peakPeriod')}
                            </Typography>
                          )}
                        </Box>
                      }
                      arrow
                    >
                      <Box
                        sx={{
                          height: 32,
                          bgcolor: color,
                          borderRadius: 1,
                          border: isPeak && status !== 'off' ? '2px solid #000' : status === 'off' ? '1px solid #e0e0e0' : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.7rem',
                          color: status === 'off' ? '#9e9e9e' : '#fff',
                          cursor: interactive ? 'pointer' : 'help',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: 2,
                          },
                        }}
                        role={interactive ? 'button' : undefined}
                        tabIndex={interactive ? 0 : -1}
                        aria-label={
                          interactive
                            ? t('autoFleetGen.timeline.toggleAria', { vehicle: vehicle.vehicleName, time: formatTime(blockStartMinutes), status })
                            : undefined
                        }
                        onClick={() => handleBlockInteraction(vehicle.vehicleId, blockStartMinutes, status)}
                        onKeyDown={(event) => {
                          if (!interactive) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleBlockInteraction(vehicle.vehicleId, blockStartMinutes, status);
                          }
                        }}
                      >
                        {getStatusIcon(status)}
                      </Box>
                    </Tooltip>
                  );
                })}
                {hasRowSuffix && (
                  <Box display="flex" alignItems="center" justifyContent="center">
                    {renderVehicleRowSuffix!(vehicle, vehicleIdx)}
                  </Box>
                )}
              </Box>
            );
          })}

          {/* Summary row - vehicles working per block */}
          <Box display="grid" gridTemplateColumns={timelineGridTemplate} gap={0.5} mt={1.5} pt={1.5} borderTop="2px solid #e0e0e0">
            <Box display="flex" flexDirection="column" justifyContent="center" px={1}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('autoFleetGen.timeline.activeVehicles')}
              </Typography>
              <Box display="flex" gap={0.5} alignItems="center">
                <Box width={12} height={12} bgcolor="#f5f5f5" border="1px solid #e0e0e0" borderRadius={0.5} />
                <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>{t('autoFleetGen.timeline.none')}</Typography>
                <Box width={12} height={12} bgcolor="#e3f2fd" borderRadius={0.5} />
                <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>{t('autoFleetGen.timeline.low')}</Typography>
                <Box width={12} height={12} bgcolor="#90caf9" borderRadius={0.5} />
                <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>{t('autoFleetGen.timeline.med')}</Typography>
                <Box width={12} height={12} bgcolor="#42a5f5" borderRadius={0.5} />
                <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>{t('autoFleetGen.timeline.high')}</Typography>
              </Box>
            </Box>
            {Array.from({ length: numBlocks }).map((_, blockIdx) => {
              const blockStartMinutes = serviceStartMinutes + blockIdx * intervalMinutes;
              const activeCount = getVehiclesWorkingAtBlock(blockStartMinutes);
              const isPeak = isPeakBlock(blockStartMinutes);
              
              // Calculate color intensity based on active vehicles (0 = light, max = dark)
              const maxVehicles = vehicles.length;
              const intensity = maxVehicles > 0 ? activeCount / maxVehicles : 0;
              
              // Create gradient from light blue to dark blue based on intensity
              const getActivityColor = (intensity: number) => {
                if (intensity === 0) return '#f5f5f5'; // Gray for no vehicles
                if (intensity < 0.33) return '#e3f2fd'; // Very light blue (low activity)
                if (intensity < 0.67) return '#90caf9'; // Medium blue (medium activity)
                return '#42a5f5'; // Bright blue (high activity)
              };

              return (
                <Box
                  key={blockIdx}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    height: 32,
                    bgcolor: getActivityColor(intensity),
                    borderRadius: 1,
                    border: isPeak ? '2px solid #000' : '1px solid #e0e0e0',
                    transition: 'background-color 0.3s ease',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 18,
                      px: 0.25,
                      color: intensity > 0.5 ? '#1976d2' : '#666',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      lineHeight: 1,
                      flex: '0 0 auto',
                    }}
                  >
                    {activeCount}
                  </Box>
                </Box>
              );
            })}
            {hasRowSuffix && <Box /> /* Empty cell aligning with the trailing suffix column */}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default FleetTimelineVisualization;
